/**
 * KolLoginPage — onboarding as a calm first conversation. The orb greets, the
 * copy speaks in the place's understated voice ("your car, in one place" — not
 * a mascot), and the single ask is one identifier at a time. Reuses the
 * existing magic-link + Google auth. Post-auth lands on the קוֹל Home (/).
 */
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { signupRequest, googleSignIn, ApiError } from '@/lib/api';
import { useAuthStore } from '@/stores/auth.store';
import { Orb } from '@/components/kol/Orb';
import { Icon } from '@/components/kol/Icon';

const PPL_CONSENT_VERSION = 'v1';
const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID as string | undefined;

export function KolLoginPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const setSession = useAuthStore((s) => s.setSession);
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [consent, setConsent] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const googleBtnRef = useRef<HTMLDivElement | null>(null);

  const lang = (i18n.language === 'en' || i18n.language === 'ar' ? i18n.language : 'he') as 'he' | 'en' | 'ar';

  useEffect(() => {
    if (!GOOGLE_CLIENT_ID) return;
    const scriptId = 'gsi-client';
    const onReady = () => {
      const gsi = window.google?.accounts?.id;
      if (!gsi || !googleBtnRef.current) return;
      gsi.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: async ({ credential }) => {
          setError(null);
          setPending(true);
          try {
            const result = await googleSignIn(credential);
            setSession({
              accessToken: result.tokens.accessToken,
              refreshToken: result.tokens.refreshToken,
              user: {
                userId: result.user.id,
                tenantId: result.user.tenantId,
                email: result.user.email,
                phone: result.user.phone,
                name: result.user.fullName,
              },
            });
            navigate('/', { replace: true });
          } catch (err) {
            setError(err instanceof ApiError ? err.message : t('common.error', { defaultValue: 'משהו השתבש' }));
          } finally {
            setPending(false);
          }
        },
      });
      gsi.renderButton(googleBtnRef.current, {
        type: 'standard',
        theme: 'outline',
        size: 'large',
        text: 'continue_with',
        width: 300,
        locale: lang === 'he' ? 'iw' : lang,
      });
    };
    if (document.getElementById(scriptId)) {
      onReady();
      return;
    }
    const s = document.createElement('script');
    s.id = scriptId;
    s.src = 'https://accounts.google.com/gsi/client';
    s.async = true;
    s.defer = true;
    s.onload = onReady;
    document.head.appendChild(s);
  }, [lang, navigate, setSession, t]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setPending(true);
    try {
      await signupRequest({
        phone,
        email,
        fullName: fullName || undefined,
        preferredLanguage: lang,
        pplConsentVersion: PPL_CONSENT_VERSION,
      });
      setSent(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.error', { defaultValue: 'משהו השתבש' }));
    } finally {
      setPending(false);
    }
  }

  return (
    <div
      className="kol-grain relative font-kol min-h-screen bg-kol-canvas text-kol-ink flex flex-col items-center px-6 pt-[max(40px,env(safe-area-inset-top))] pb-[max(24px,env(safe-area-inset-bottom))]"
      dir={i18n.dir()}
    >
      <div className="relative z-[1] w-full max-w-md flex-1 flex flex-col">
        <div className="flex flex-col items-center text-center gap-4 pt-8">
          <Orb state="idle" size={88} />
          <div>
            <h1 className="font-kol-display text-[30px] font-medium tracking-tight leading-[1.15]">
              {t('kol.login.hero', { defaultValue: 'הרכב שלכם, במקום אחד' })}
            </h1>
            <p className="text-[15.5px] text-kol-ink-soft leading-relaxed mt-2.5">
              {t('kol.login.sub', { defaultValue: 'צלמו את המסמכים פעם אחת. אני אזכור הכול, ואענה על כל שאלה.' })}
            </p>
          </div>
        </div>

        {sent ? (
          <div className="mt-9 rounded-[22px] bg-kol-sage-soft border border-kol-sage/30 p-6 text-center">
            <div className="text-[34px] mb-2" aria-hidden>
              ✉️
            </div>
            <p className="text-[17px] font-semibold text-kol-ink leading-relaxed">
              {t('kol.login.sent', { defaultValue: 'שלחנו לכם קישור כניסה. פתחו אותו מהטלפון הזה.' })}
            </p>
          </div>
        ) : (
          <div className="mt-8 flex flex-col">
            {GOOGLE_CLIENT_ID && (
              <>
                <div
                  ref={googleBtnRef}
                  className="flex justify-center"
                  aria-label={t('kol.login.google', { defaultValue: 'כניסה עם Google' })}
                />
                <div className="relative my-6" aria-hidden>
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-kol-line" />
                  </div>
                  <div className="relative flex justify-center">
                    <span className="bg-kol-canvas px-3 text-[13px] text-kol-ink-faint">
                      {t('kol.login.or', { defaultValue: 'או' })}
                    </span>
                  </div>
                </div>
              </>
            )}

            <form onSubmit={onSubmit} className="flex flex-col gap-3.5">
              <Field
                label={t('kol.login.phone', { defaultValue: 'מספר טלפון' })}
                type="tel"
                inputMode="tel"
                autoComplete="tel"
                required
                value={phone}
                onChange={setPhone}
              />
              <Field
                label={t('kol.login.email', { defaultValue: 'אימייל' })}
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={setEmail}
              />
              <Field
                label={t('kol.login.name', { defaultValue: 'שם (לא חובה)' })}
                type="text"
                autoComplete="name"
                value={fullName}
                onChange={setFullName}
              />

              <label className="flex items-start gap-3 text-[14px] text-kol-ink-soft leading-relaxed mt-1">
                <input
                  type="checkbox"
                  required
                  checked={consent}
                  onChange={(e) => setConsent(e.target.checked)}
                  className="mt-1 size-5 rounded border-kol-line accent-kol-pine"
                />
                <span>{t('kol.login.consent', { defaultValue: 'אני מאשר/ת את תנאי השימוש ומדיניות הפרטיות.' })}</span>
              </label>

              {error && (
                <p role="alert" className="text-[14px] text-kol-clay-ink">
                  {error}
                </p>
              )}

              <button
                type="submit"
                disabled={pending || !consent}
                className="mt-2 w-full rounded-[20px] bg-kol-pine text-white font-semibold py-4 text-[16.5px] min-h-[54px] shadow-[0_16px_30px_-18px_rgba(31,80,66,0.75)] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {pending
                  ? t('common.loading', { defaultValue: 'רגע…' })
                  : t('kol.login.submit', { defaultValue: 'שליחת קישור כניסה' })}
              </button>

              <div className="flex items-center justify-center gap-2 text-kol-ink-faint text-[12.5px] mt-1">
                <Icon name="lock" size={15} />
                {t('kol.login.privacy', { defaultValue: 'ללא סיסמה · המידע שלכם נשאר שלכם' })}
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}

function Field({
  label,
  value,
  onChange,
  ...rest
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange'>) {
  return (
    <label className="block">
      <span className="block text-[14px] font-medium text-kol-ink-soft mb-1.5">{label}</span>
      <input
        {...rest}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-2xl border border-kol-line bg-kol-card px-4 py-3.5 text-[16px] text-kol-ink focus:outline-none focus:ring-2 focus:ring-kol-pine min-h-[52px]"
      />
    </label>
  );
}
